import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, EyeOff, X, Users, ShieldOff, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/** TikTok/Instagram-style live chat: scrolling comment feed + input, with
 *  owner moderation (hide a comment for everyone, ban a viewer from the
 *  live) and a personal "hide from my view only" toggle for any viewer. */
export default function LiveComments({ liveId, isOwner }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [locallyHidden, setLocallyHidden] = useState(() => new Set());
  const [showViewers, setShowViewers] = useState(false);
  const lastSeenRef = useRef(new Date(0).toISOString());
  const listRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/lives/${liveId}/comments`, { params: { since: lastSeenRef.current, limit: 50 } });
        if (!mounted || !data.length) return;
        lastSeenRef.current = data[data.length - 1].created_at;
        setComments((prev) => [...prev, ...data].slice(-200));
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 2500);
    return () => { mounted = false; clearInterval(id); };
  }, [liveId]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments]);

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !user) return;
    setText("");
    try {
      const { data } = await api.post(`/lives/${liveId}/comments`, { text: value });
      setComments((prev) => [...prev, data].slice(-200));
      lastSeenRef.current = data.created_at;
    } catch (e) {
      toast.error(e.response?.data?.detail === "You are banned from this live" ? "Vous avez été banni de ce live" : "Message non envoyé");
    }
  };

  const hideForMe = (id) => {
    setLocallyHidden((prev) => new Set(prev).add(id));
  };

  const hideForEveryone = async (id) => {
    setComments((prev) => prev.filter((c) => c.id !== id));
    try { await api.post(`/lives/${liveId}/comments/${id}/hide`); } catch { /* best-effort */ }
  };

  const visible = comments.filter((c) => !locallyHidden.has(c.id));

  return (
    <div className="absolute left-3 right-24 bottom-24 z-20 flex flex-col pointer-events-none" style={{ maxHeight: "38vh" }}>
      {isOwner && (
        <button
          onClick={() => setShowViewers(true)}
          className="pointer-events-auto self-start mb-2 inline-flex items-center gap-1.5 chip bg-black/60 text-white border border-white/10"
          data-testid="live-viewers-btn"
        >
          <Users className="w-3 h-3" /> Spectateurs
        </button>
      )}

      <div ref={listRef} className="pointer-events-auto flex-1 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar" data-testid="live-comments-list">
        {visible.map((c) => (
          <div key={c.id} className="group flex items-start gap-1.5 bg-black/40 rounded-xl px-2.5 py-1.5 max-w-full w-fit" data-testid={`live-comment-${c.id}`}>
            <span className="text-xs font-bold text-[#00F2FE] shrink-0">{c.user_name}</span>
            <span className="text-xs text-white break-words">{c.text}</span>
            <button onClick={() => hideForMe(c.id)} className="shrink-0 opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-white transition" aria-label="hide-for-me" data-testid={`comment-hide-me-${c.id}`}>
              <EyeOff className="w-3 h-3" />
            </button>
            {isOwner && (
              <button onClick={() => hideForEveryone(c.id)} className="shrink-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition" aria-label="hide-for-everyone" data-testid={`comment-hide-all-${c.id}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {user && (
        <form onSubmit={send} className="pointer-events-auto mt-2 flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Écrire un commentaire..."
            maxLength={300}
            className="flex-1 bg-black/50 border border-white/15 rounded-full px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
            data-testid="live-comment-input"
          />
          <button type="submit" disabled={!text.trim()} className="w-9 h-9 shrink-0 rounded-full bg-white text-black grid place-items-center disabled:opacity-40" data-testid="live-comment-send">
            <Send className="w-4 h-4" />
          </button>
        </form>
      )}

      {showViewers && <ViewersPanel liveId={liveId} onClose={() => setShowViewers(false)} />}
    </div>
  );
}

function ViewersPanel({ liveId, onClose }) {
  const [viewers, setViewers] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/lives/${liveId}/viewers-list`); setViewers(data); } catch { setViewers([]); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveId]);

  const toggleBan = async (v) => {
    setBusyId(v.id);
    try {
      await api.post(`/lives/${liveId}/viewers/${v.id}/${v.banned ? "unban" : "ban"}`);
      await load();
    } catch { toast.error("Action impossible"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="pointer-events-auto fixed inset-0 z-[70] bg-black/85 backdrop-blur-xl grid place-items-center p-4" data-testid="live-viewers-panel">
      <div className="w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl p-5 max-h-[70vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close">
          <X className="w-4 h-4 text-zinc-400" />
        </button>
        <h3 className="text-lg font-black font-display text-white flex items-center gap-2"><Users className="w-4 h-4" /> Spectateurs présents</h3>
        {viewers === null && <p className="text-zinc-500 text-sm mt-4">Chargement...</p>}
        {viewers && viewers.length === 0 && <p className="text-zinc-500 text-sm mt-4">Personne pour le moment.</p>}
        <div className="mt-4 space-y-2">
          {viewers?.map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-2 bg-white/5 rounded-xl px-3 py-2" data-testid={`viewer-row-${v.id}`}>
              <span className="text-sm text-white truncate">{v.name}</span>
              <button
                onClick={() => toggleBan(v)}
                disabled={busyId === v.id}
                className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold disabled:opacity-50 ${v.banned ? "bg-white/10 text-zinc-300" : "bg-red-500/15 text-red-400"}`}
                data-testid={`viewer-ban-${v.id}`}
              >
                {v.banned ? <><ShieldOff className="w-3 h-3" /> Débannir</> : <><ShieldAlert className="w-3 h-3" /> Bannir</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
